/**
 * @fileoverview A modal component that allows users to enter a prompt for AI post generation.
 */
import React, { useState, useEffect } from 'react';
import { XCircleIcon } from './icons';

interface GenerateSuggestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (prompt: string) => Promise<void>;
  isGenerating: boolean; // Loading state passed from the parent.
}

export const GenerateSuggestionModal: React.FC<GenerateSuggestionModalProps> = ({ isOpen, onClose, onGenerate, isGenerating }) => {
  // State for the user's input prompt.
  const [prompt, setPrompt] = useState('');
  // State to manage the closing animation.
  const [isClosing, setIsClosing] = useState(false);

  // Effect to reset the prompt and animation state when the modal opens.
  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
      setPrompt('');
    }
  }, [isOpen]);

  // Handles closing the modal with a fade-out animation.
  const handleClose = () => {
    if (isGenerating) return; // Prevent closing while generating.
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 200); // Animation duration
  };
  
  // Triggers the generation process in the parent component.
  const handleGenerateClick = async () => {
    if (!prompt.trim() || isGenerating) return;
    await onGenerate(prompt);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div 
      className={`fixed inset-0 bg-slate-900/50 dark:bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`}
      onClick={handleClose}
      aria-modal="true"
      role="dialog"
    >
      <div 
        className={`bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-slate-200 dark:border-gray-700 w-full max-w-lg relative ${isClosing ? 'animate-fade-out-scale' : 'animate-fade-in-scale'}`}
        onClick={e => e.stopPropagation()}
      >
        <button 
          onClick={handleClose} 
          className="absolute top-4 right-4 text-slate-400 dark:text-gray-400 hover:text-slate-800 dark:hover:text-white transition-colors"
          aria-label="Close"
        >
          <XCircleIcon className="w-8 h-8" />
        </button>

        <h2 className="text-xl font-bold mb-2">Generate Post with AI</h2>
        <p className="text-sm text-slate-500 dark:text-gray-400 mb-4">
            Enter a topic, a few keywords, or a rough draft, and let AI create a post for you.
        </p>
        
        <textarea
            id="suggestion-prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="e.g., 'launching a new productivity app next week'"
            className="w-full h-36 bg-slate-100 dark:bg-gray-700 text-slate-800 dark:text-gray-200 rounded-lg p-3 border border-slate-300 dark:border-gray-600 focus:ring-2 focus:outline-none transition focus:ring-blue-500 dark:focus:ring-blue-400"
            rows={5}
            disabled={isGenerating}
            autoFocus
          />

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={handleClose}
            disabled={isGenerating}
            className="bg-slate-200 hover:bg-slate-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-slate-700 dark:text-gray-200 font-bold py-2 px-4 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleGenerateClick}
            disabled={isGenerating || !prompt.trim()}
            className="w-32 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isGenerating ? (
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : 'Generate'}
          </button>
        </div>
      </div>
       {/* CSS for animations */}
       <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes fade-out {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        @keyframes fade-in-scale {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes fade-out-scale {
          from { opacity: 1; transform: scale(1); }
          to { opacity: 0; transform: scale(0.95); }
        }
        .animate-fade-in { animation: fade-in 0.2s ease-out forwards; }
        .animate-fade-out { animation: fade-out 0.2s ease-in forwards; }
        .animate-fade-in-scale { animation: fade-in-scale 0.2s ease-out forwards; }
        .animate-fade-out-scale { animation: fade-out-scale 0.2s ease-in forwards; }
      `}</style>
    </div>
  );
};
